class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);

    if (this.notification) {
      this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
      this.querySelectorAll('.cart-notification__close, [data-cart-close]').forEach((btn) =>
        btn.addEventListener('click', this.close.bind(this))
      );
      // Any user interaction inside the popover promotes it to sticky mode.
      this.notification.addEventListener('click', () => {
        this.openMode = 'sticky';
      });
    }

    // Mode set by whoever opens us: 'sticky' (post-add, X/outside click closes)
    // or 'hover' (mouse-leave-with-delay closes).
    this.openMode = null;

    // Keep the popover's items list in sync with cart changes that originate
    // elsewhere (e.g. the /cart page). The popup's own qty editor already
    // updates itself via cart-notification-items.js, so we only need to
    // refresh when the source isn't us.
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this._cartUpdateUnsub = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        // Don't fight active typing/clicking inside our own popover.
        if (this.notification && this.notification.contains(document.activeElement)) return;
        this.refreshFromCart();
      });
    }
  }

  async refreshFromCart() {
    const target = document.getElementById('cart-notification-product');
    if (!target) return;
    try {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const res = await fetch(`${root}?sections=cart-notification-product,cart-notification-footer`, {
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) return;
      const json = await res.json();
      if (!json) return;

      const itemsHtml = json['cart-notification-product'];
      if (itemsHtml) {
        const inner = this.getSectionInnerHTML(itemsHtml, '.shopify-section');
        if (inner) target.innerHTML = inner;
      }

      const footer = document.getElementById('cart-notification-footer');
      const footerHtml = json['cart-notification-footer'];
      if (footer && footerHtml != null) {
        footer.innerHTML = this.getSectionInnerHTML(footerHtml, '.shopify-section');
      }
    } catch (e) {
      // Silent — stale content beats broken JS.
    }
  }

  open(mode) {
    if (!this.notification) return;

    // A product image is mid-flight towards the cart icon — let it land before
    // the drawer slides over the top of it. See cart-fly.js.
    const flyWait = (window.__cartFlyUntil || 0) - Date.now();
    if (mode === 'sticky' && flyWait > 0) {
      clearTimeout(this._flyOpenTimer);
      this._flyOpenTimer = setTimeout(() => this.open(mode), flyWait);
      return;
    }

    if (mode) this.openMode = mode;

    // Reflect mode on the dialog so CSS can hide the "Item added to your cart"
    // heading when we're just previewing the cart on hover.
    if (this.openMode) this.notification.setAttribute('data-open-mode', this.openMode);

    this.notification.hidden = false;
    // Force reflow so the transition runs from hidden state.
    void this.notification.offsetWidth;
    this.notification.classList.add('animate', 'active');

    const backdrop = this.querySelector('.cart-notification__backdrop');
    if (backdrop) backdrop.classList.add('active');
    document.body.classList.add('cart-drawer-open');

    if (this.openMode === 'sticky') {
      // Listen for outside clicks only in sticky mode.
      document.body.addEventListener('click', this.onBodyClick);
    }

    if (this.header && typeof this.header.reveal === 'function') this.header.reveal();
  }

  close() {
    if (!this.notification) return;
    clearTimeout(this._flyOpenTimer);
    this.notification.classList.remove('active');

    const backdrop = this.querySelector('.cart-notification__backdrop');
    if (backdrop) backdrop.classList.remove('active');
    document.body.classList.remove('cart-drawer-open');
    document.body.removeEventListener('click', this.onBodyClick);
    this.openMode = null;
    this.notification.removeAttribute('data-open-mode');
    setTimeout(() => {
      if (!this.notification.classList.contains('active')) this.notification.hidden = true;
    }, 200);

    if (this.activeElement) {
      try {
        // Detach the focus-trap listeners WITHOUT letting removeTrapFocus call
        // .focus() for us: a bare focus() scrolls the element into view, which
        // yanked the page down to wherever Add to cart happened to be. Restore
        // focus ourselves with preventScroll, and only if the element is still
        // in the document.
        removeTrapFocus();
        if (this.activeElement.isConnected) {
          this.activeElement.focus({ preventScroll: true });
        }
      } catch (e) {
        /* noop */
      }
      // Clear it so a later open from the cart icon doesn't restore focus to a
      // stale Add to cart button from a previous page.
      this.activeElement = null;
    }
  }

  isOpen() {
    return this.notification && this.notification.classList.contains('active');
  }

  renderContents(parsedState) {
    this.cartItemKey = parsedState.key;
    this.getSectionsToRender().forEach((section) => {
      const target = document.getElementById(section.id);
      if (!target) return;
      const sectionHtml = parsedState.sections && parsedState.sections[section.id];
      if (sectionHtml == null) return;
      target.innerHTML = this.getSectionInnerHTML(sectionHtml, section.selector);
    });

    // Post-add: open in sticky mode.
    this.open('sticky');
  }

  getSectionsToRender() {
    return [
      { id: 'cart-notification-product' },
      { id: 'cart-notification-footer' },
      { id: 'cart-icon-bubble' },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (!html) return '';
    const node = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return node ? node.innerHTML : '';
  }

  handleBodyClick(evt) {
    if (!this.notification || !this.notification.classList.contains('active')) return;
    const popover = this.notification;
    const cartIcon = document.getElementById('cart-icon-bubble');
    // Use geometric + element containment so qty controls inside don't trigger close.
    if (popover.contains(evt.target)) return;
    if (cartIcon && cartIcon.contains(evt.target)) return;
    // Geometric fallback for synthetic events.
    const rect = popover.getBoundingClientRect();
    if (
      evt.clientX >= rect.left &&
      evt.clientX <= rect.right &&
      evt.clientY >= rect.top &&
      evt.clientY <= rect.bottom
    ) {
      return;
    }
    this.close();
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-notification', CartNotification);
